import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { amt, DayBook, errorText, fmtDate, localDate, money, reportsApi, VOUCHER_TYPES, VoucherType } from '../accounting.api';
import BooksHeader, { RangeFields } from '../components/BooksHeader';
import { useBooksCompany } from '../components/CompanySelect';
import VoucherViewer, { CancelledChip, TypeChip } from '../components/VoucherViewer';

const num = { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } as const;

const shiftDay = (iso: string, by: number) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + by);
  return localDate(d);
};

/** Every voucher of a day (or a range) with its lines, as Tally's Day Book. A voucher opens on click. */
export default function DayBookPage() {
  const { companies, companyId, setCompanyId, loaded } = useBooksCompany();
  const [day, setDay] = useState(localDate());
  const [ranged, setRanged] = useState(false);
  const [range, setRange] = useState({ from: localDate(), to: localDate() });
  const [type, setType] = useState<VoucherType | ''>('');
  const [book, setBook] = useState<DayBook | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);

  const from = ranged ? range.from : day;
  const to = ranged ? range.to : day;

  const load = useCallback(() => {
    if (!companyId || !from || !to) return;
    setLoading(true);
    setError(null);
    reportsApi
      .dayBook(companyId, { from, to }, type)
      .then(setBook)
      .catch((e) => {
        setBook(null);
        setError(errorText(e, 'Could not load the day book', 'ACCOUNTING_DAY_BOOK_FAILED'));
      })
      .finally(() => setLoading(false));
  }, [companyId, from, to, type]);

  useEffect(load, [load]);

  return (
    <Stack gap={3}>
      <BooksHeader title='DAY BOOK' companies={companies} companyId={companyId} onCompany={setCompanyId} loaded={loaded}>
        {ranged ? (
          <RangeFields value={range} onChange={setRange} />
        ) : (
          <Stack direction='row' alignItems='center' gap={0.5}>
            <IconButton size='small' aria-label='Previous day' onClick={() => setDay(shiftDay(day, -1))}>
              <Icon icon='mdi:chevron-left' />
            </IconButton>
            <TextField
              label='Date'
              type='date'
              size='small'
              value={day}
              onChange={(e) => setDay(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <IconButton size='small' aria-label='Next day' onClick={() => setDay(shiftDay(day, 1))}>
              <Icon icon='mdi:chevron-right' />
            </IconButton>
          </Stack>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={ranged}
              onChange={(e) => {
                setRanged(e.target.checked);
                if (e.target.checked) setRange({ from: day, to: day });
              }}
            />
          }
          label='Range'
        />
        <TextField select size='small' label='Type' value={type} onChange={(e) => setType(e.target.value as VoucherType | '')} sx={{ minWidth: 130 }}>
          <MenuItem value=''>All types</MenuItem>
          {VOUCHER_TYPES.map((t) => (
            <MenuItem key={t.type} value={t.type}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
      </BooksHeader>

      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}

      {book && (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Particulars</TableCell>
                <TableCell align='right'>Debit</TableCell>
                <TableCell align='right'>Credit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {book.vouchers.length === 0 && (
                <TableRow>
                  <TableCell {...({ colSpan: 4 } as object)}>
                    <Typography variant='body2' color='text.secondary'>
                      No vouchers {from === to ? `on ${fmtDate(from)}` : 'in this range'}.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {book.vouchers.map((v) => {
                const cancelled = v.status === 'CANCELLED';
                const muted = cancelled ? { color: 'text.disabled', textDecoration: 'line-through' } : {};
                return (
                  <Fragment key={v.id}>
                    <TableRow hover onClick={() => setViewing(v.id)} sx={{ cursor: 'pointer', bgcolor: 'grey.50', '& td': { borderBottom: 0 } }}>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtDate(v.date)}</TableCell>
                      <TableCell {...({ colSpan: 3 } as object)}>
                        <Stack direction='row' gap={1} alignItems='center' flexWrap='wrap'>
                          <Typography variant='body2' fontWeight={700} sx={muted}>
                            {v.voucherNo}
                          </Typography>
                          <TypeChip type={v.type} />
                          {cancelled && <CancelledChip />}
                          {v.referenceNo && (
                            <Typography variant='caption' color='text.secondary'>
                              Ref. {v.referenceNo}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {v.lines.map((l) => (
                      <TableRow key={`${v.id}-${l.lineNo}`} hover onClick={() => setViewing(v.id)} sx={{ cursor: 'pointer', '& td': { borderBottom: 0 } }}>
                        <TableCell />
                        <TableCell sx={{ pl: 4, ...muted }}>
                          {Number(l.debit) > 0 ? 'Dr ' : 'Cr '}
                          {l.ledgerName}
                        </TableCell>
                        <TableCell align='right' sx={{ ...num, ...muted }}>
                          {amt(l.debit)}
                        </TableCell>
                        <TableCell align='right' sx={{ ...num, ...muted }}>
                          {amt(l.credit)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell />
                      <TableCell {...({ colSpan: 3 } as object)}>
                        {v.narration && (
                          <Typography variant='caption' color='text.secondary'>
                            {v.narration}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
              {book.vouchers.length > 0 && (
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell {...({ colSpan: 2 } as object)} sx={{ fontWeight: 700 }}>
                    Total ({book.vouchers.filter((v) => v.status === 'POSTED').length} posted)
                  </TableCell>
                  <TableCell align='right' sx={{ ...num, fontWeight: 700 }}>
                    {money(book.totalDebit)}
                  </TableCell>
                  <TableCell align='right' sx={{ ...num, fontWeight: 700 }}>
                    {money(book.totalCredit)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <VoucherViewer companyId={companyId} voucherId={viewing} onClose={() => setViewing(null)} onChanged={load} />
    </Stack>
  );
}
